import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Button,
    Box,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import useGetDropdownValues from 'routes/Pages/Dockets/CustomHooks/useGetDropdownValues';

const ALL_THREE_ROLES = ['admin', 'haulageadmin'];

const STATUS_ACTIVE = 1;
const STATUS_OPTIONS = [
    { value: 1, label: 'Active' },
    { value: 0, label: 'Inactive' },
];

const isDriverRole = role => role === 'driver' || role === 'subcontractor' || role?.startsWith('subcontractor');

// Same role -> flag rules as Add User
const getFlagsForRole = roleName => {
    const role = roleName?.toLowerCase().trim();
    const flags = { isDriver: 0, isLoader: 0, isOperator: 0 };

    if (ALL_THREE_ROLES.includes(role)) {
        flags.isDriver = 1;
        flags.isLoader = 1;
        flags.isOperator = 1;
    } else if (isDriverRole(role)) {
        flags.isDriver = 1;
    } else if (role === 'operator') {
        flags.isOperator = 1;
    }
    return flags;
};


// Accepts strings or { label, value? } objects and returns plain strings
const toLabel = o => (typeof o === 'string' ? o : o?.label ?? o?.value ?? '');

const OptionField = ({ label, name, value, options = [], onChange }) => {
    const labels = (options || []).map(toLabel).filter(Boolean);
    // keep a saved value selectable even if it's no longer in the list
    const items = value && !labels.includes(value) ? [value, ...labels] : labels;

    return (
        <TextField
            select
            label={label}
            name={name}
            value={value}
            onChange={onChange}
            fullWidth
            SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}>
            <MenuItem value="">
                <em>None</em>
            </MenuItem>
            {items.map(o => (
                <MenuItem key={o} value={o}>
                    {o}
                </MenuItem>
            ))}
        </TextField>
    );
};

const EditUserDialog = ({ open, user, roles = [], onClose, onSave }) => {

    const trucks = useGetDropdownValues('truck');
    const trailers = useGetDropdownValues('trailer');
    const loaders = useGetDropdownValues('LoaderMachineTruck');

    const [form, setForm] = useState({
        displayName: '',
        roleName: '',
        isDriver: 0,
        isLoader: 0,
        isOperator: 0,
        defaultTruck: '',
        defaultLoader: '',
        defaultTrailer: '',
        userStatus: STATUS_ACTIVE,
    });
    const [saving, setSaving] = useState(false);

    // Load the selected user's current values each time the dialog opens
    useEffect(() => {
        if (open && user) {
            const roleFlags = getFlagsForRole(user.roleName);
            const roleIsLocked = ALL_THREE_ROLES.includes(user.roleName?.toLowerCase().trim());

            const pick = key => {
                if (roleIsLocked) return roleFlags[key]; // admin / haulageadmin: always all 1
                if (user[key] === undefined || user[key] === null) return roleFlags[key]; // API didn't send it
                return user[key] ? 1 : 0;
            };

            setForm({
                displayName: user.displayName || '',
                roleName: user.roleName || '',
                isDriver: pick('isDriver'),
                isLoader: pick('isLoader'),
                isOperator: pick('isOperator'),
                defaultTruck: user.defaultTruck || '',
                defaultLoader: user.defaultLoader || '',
                defaultTrailer: user.defaultTrailer || '',
                // Never guess the status: if the API didn't send it, force the admin to pick one
                userStatus: user.userStatus ?? STATUS_ACTIVE,
            });
        }
    }, [open, user]);

    const role = form.roleName?.toLowerCase().trim();
    const lockAll = ALL_THREE_ROLES.includes(role);

    const handleChange = e => {
        const { name, value } = e.target;

        setForm(prev => {
            const updated = { ...prev, [name]: value };
            // Only re-derive flags when the role itself is changed by the user
            if (name === 'roleName') {
                Object.assign(updated, getFlagsForRole(value));
            }
            return updated;
        });
    };

    const handleCheckboxChange = e => {
        const { name, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: checked ? 1 : 0 }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            // Keys match EditUserModel exactly
            await onSave({
                id: user.userId,
                displayName: form.displayName.trim(),
                roleName: form.roleName,
                defaultTruck: form.defaultTruck,
                defaultLoader: form.defaultLoader,
                defaultTrailer: form.defaultTrailer,
                isDriver: form.isDriver,
                isLoader: form.isLoader,
                isOperator: form.isOperator,
                userStatus: Number(form.userStatus),
            });
        } catch (err) {
            console.error('EditUser failed', err); // parent shows the error message
        } finally {
            setSaving(false);
        }
    };

    const canSave = !saving && form.displayName.trim() && form.roleName && form.userStatus !== '';

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
            <DialogTitle sx={{ fontWeight: 700 }}>Edit User</DialogTitle>

            <DialogContent>
                <Box display="flex" flexDirection="column" gap={3} sx={{ mt: 1 }}>
                    <Box display="flex" gap={2} flexWrap="wrap" sx={{ mt: 2 }}>
                        <Box flex={1} minWidth={250}>
                            <TextField label="Email" value={user?.email || ''} fullWidth disabled />
                        </Box>
                        <Box flex={1} minWidth={250}>
                            <TextField
                                label="Display Name"
                                name="displayName"
                                value={form.displayName}
                                onChange={handleChange}
                                fullWidth
                                required
                            />
                        </Box>
                    </Box>

                    <Box display="flex" gap={2} flexWrap="wrap">
                        <Box flex={1} minWidth={250}>
                            <TextField
                                select
                                label="Role"
                                name="roleName"
                                value={form.roleName}
                                onChange={handleChange}
                                fullWidth
                                required
                                SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}>
                                {roles.map(r => (
                                    <MenuItem key={r} value={r}>
                                        {r}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                        <Box flex={1} minWidth={250}>
                            <TextField
                                select
                                label="Status"
                                name="userStatus"
                                value={form.userStatus}
                                onChange={handleChange}
                                fullWidth
                                required>
                                {STATUS_OPTIONS.map(s => (
                                    <MenuItem key={s.value} value={s.value}>
                                        {s.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                    </Box>

                    <Box display="flex" gap={2} flexWrap="wrap">
                        <Box flex={1} minWidth={160}>
                            <OptionField label="Default Truck" name="defaultTruck" value={form.defaultTruck}
                                options={trucks} onChange={handleChange} />
                        </Box>
                        <Box flex={1} minWidth={160}>
                            <OptionField label="Default Loader" name="defaultLoader" value={form.defaultLoader}
                                options={loaders} onChange={handleChange} />
                        </Box>
                        <Box flex={1} minWidth={160}>
                            <OptionField label="Default Trailer" name="defaultTrailer" value={form.defaultTrailer}
                                options={trailers} onChange={handleChange} />
                        </Box>
                    </Box>

                    <Box display="flex" gap={3} flexWrap="wrap">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.isDriver === 1}
                                    onChange={handleCheckboxChange}
                                    name="isDriver"
                                    disabled={isDriverRole(role) || lockAll}
                                />
                            }
                            label="Is Driver"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.isLoader === 1}
                                    onChange={handleCheckboxChange}
                                    name="isLoader"
                                    disabled={lockAll}
                                />
                            }
                            label="Is Loader"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.isOperator === 1}
                                    onChange={handleCheckboxChange}
                                    name="isOperator"
                                    disabled={role === 'operator' || lockAll}
                                />
                            }
                            label="Is Operator"
                        />
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} sx={{ textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={!canSave} sx={{ textTransform: 'none' }}>
                    Save User
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditUserDialog;